import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useEffect, useState } from "react";
import { API_LOGIN_REQUEST } from "../auth/msal";
import { teamsAuth } from "./teams-auth";
import { logger } from "./logger";

export interface AuthContext {
  isInTeams: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  account: any;
  getToken: () => Promise<string>;
  login: () => Promise<void>;
  logout: () => void;
}

export function useUnifiedAuth(): AuthContext {
  const { instance, accounts } = useMsal();
  const [isInTeams, setIsInTeams] = useState(false);
  const [teamsAccount, setTeamsAccount] = useState<any>(null);
  const [teamsInitialized, setTeamsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const msalAccount = accounts[0];

  // Initialize Teams context
  useEffect(() => {
    const initTeams = async () => {
      try {
        const inTeams = await teamsAuth.initialize();
        setIsInTeams(inTeams);
        
        if (inTeams) {
          console.log('Teams detected, getting user info...');
          try {
            // Get Teams user info
            const userInfo = await teamsAuth.getUserInfo();
            console.log('Teams user info:', userInfo);
            setTeamsAccount(userInfo);
          } catch (userInfoError) {
            console.warn('Could not get Teams user info, but continuing:', userInfoError);
            // Set a minimal account object so authentication still works
            setTeamsAccount({ id: 'teams-user' });
          }
        }
      } catch (error) {
        console.log('Teams initialization failed:', error);
        setIsInTeams(false);
      } finally {
        setTeamsInitialized(true);
      }
    };

    if (!teamsInitialized) {
      initTeams();
    }
  }, [teamsInitialized]);

  // Auto-authenticate when Teams is initialized
  useEffect(() => {
    if (teamsInitialized && isInTeams && !teamsAccount) {
      logger.debug('Teams initialized but not authenticated, attempting silent SSO', undefined, 'AUTH');
      setIsLoading(true);
      
      // Try to silently get Teams SSO token
      teamsAuth.getAuthToken()
        .then((token) => {
          logger.debug('Silent Teams SSO successful', { tokenLength: token.length }, 'AUTH');
          // Try to get user info now that we have a token
          return teamsAuth.getUserInfo();
        })
        .then((userInfo) => {
          logger.debug('Got Teams user info', { hasDisplayName: !!userInfo?.displayName }, 'AUTH');
          setTeamsAccount(userInfo);
        })
        .catch((error) => {
          logger.warn('Silent Teams SSO failed, user will need to click button', error, 'AUTH');
          // Don't set teamsAccount - leave it null so button appears
          // The button will trigger manual consent flow
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [teamsInitialized, isInTeams, teamsAccount]);

  const getToken = async (): Promise<string> => {
    // Check host context directly from teamsAuth service to avoid state timing issues
    // This ensures we use the correct auth method even if the React state hasn't updated yet.
    // Uses isInM365Host() rather than isInTeams so a framed host whose handshake failed
    // still attempts host SSO instead of falling through to an MSAL popup that a mobile
    // webview will block.
    const isInM365Host = teamsAuth.isInM365Host();

    if (isInM365Host) {
      // Use host SSO
      try {
        return await teamsAuth.getAuthToken();
      } catch (teamsError) {
        logger.error('Teams SSO failed, checking for fallback options', teamsError, 'AUTH');
        // If Teams token fails and we have MSAL account, try that as fallback
        // This handles edge cases where Teams context exists but SSO fails
        if (msalAccount) {
          logger.warn('Falling back to MSAL authentication', undefined, 'AUTH');
          // Fall through to MSAL logic below
        } else {
          throw teamsError; // No fallback available
        }
      }
    }
    
    // Use MSAL (either not in Teams, or Teams failed and we're falling back)
    if (!msalAccount) throw new Error("Not signed in");
    try {
      const res = await instance.acquireTokenSilent({ 
        account: msalAccount, 
        scopes: API_LOGIN_REQUEST.scopes,
      });
      return res.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        try {
          const res = await instance.acquireTokenPopup(API_LOGIN_REQUEST);
          return res.accessToken;
        } catch (popupError) {
          console.warn('Popup token acquisition failed, trying redirect:', popupError);
          // Fallback to redirect if popup fails
          await instance.acquireTokenRedirect(API_LOGIN_REQUEST);
          throw new Error('Token acquisition redirected - page will reload');
        }
      }
      throw e;
    }
  };

  const login = async (): Promise<void> => {
    logger.debug('Login button clicked', { isInTeams, isAuthenticated, isLoading, hasAccount: !!teamsAccount }, 'AUTH');
    
    setIsLoading(true);
    try {
      if (isInTeams) {
        // For Teams, we need to trigger the SSO flow
        // This will call getAuthToken which may prompt for consent
        logger.debug('Teams SSO: Initiating manual authentication from button click', undefined, 'AUTH');
        
        try {
          // Try to get a token to trigger SSO consent if needed
          logger.debug('Calling getToken', undefined, 'AUTH');
          const token = await getToken();
          logger.debug('Teams SSO: Authentication successful', { tokenLength: token.length }, 'AUTH');
          
          // If we got a token but don't have user info yet, try to get it
          if (!teamsAccount || !teamsAccount.displayName) {
            try {
              const userInfo = await teamsAuth.getUserInfo();
              setTeamsAccount(userInfo);
            } catch (err) {
              console.warn('Could not get full user info, using minimal account');
              setTeamsAccount({ id: 'teams-user' });
            }
          }
        } catch (tokenError: any) {
          console.error('Teams SSO failed:', tokenError);
          // If token acquisition fails, still set minimal account
          // The actual API calls will handle auth errors
          if (tokenError.message?.includes('ConsentRequired')) {
            alert('Admin consent is required for this app. Please contact your administrator.');
          }
          throw tokenError;
        }
      } else {
        // For web, try popup first (works on desktop)
        // Fall back to redirect for mobile or if popup is blocked
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        if (isMobile) {
          // On mobile, use redirect directly (more reliable)
          console.log('Mobile detected, using redirect authentication');
          await instance.loginRedirect(API_LOGIN_REQUEST);
        } else {
          // On desktop, try popup first
          try {
            console.log('Desktop detected, trying popup authentication');
            await instance.loginPopup(API_LOGIN_REQUEST);
          } catch (error) {
            console.warn('Popup login failed, falling back to redirect:', error);
            await instance.loginRedirect(API_LOGIN_REQUEST);
          }
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    if (!isInTeams) {
      try {
        instance.logoutPopup();
      } catch (error) {
        console.warn('Popup logout failed, trying redirect:', error);
        instance.logoutRedirect();
      }
    }
    // Teams apps typically don't have logout functionality
  };

  const isAuthenticated = isInTeams ? !!teamsAccount : !!msalAccount;
  const account = isInTeams ? teamsAccount : msalAccount;

  return {
    isInTeams,
    isAuthenticated,
    isLoading,
    account,
    getToken,
    login,
    logout
  };
}

// Legacy hook for backward compatibility
export function useApiToken() {
  const auth = useUnifiedAuth();
  return {
    account: auth.account,
    getToken: auth.getToken
  };
}
